import { useEffect, useCallback } from "react";
import { useStore } from "@nanostores/react";
import { BrowserRouter, Route, Switch, Redirect } from "react-router-dom";
import { app } from "lib/firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  userProfile,
  setUserData,
  setIsAuthenticated,
  setLoading,
  setDocuments,
} from "contexts/userProfile.js";

import AuthLayout from "layouts/Auth";
import AdminLayout from "layouts/Admin";
import LandingLayout from "layouts/Landing";
import Home from "views/Home";
import Lottie from "lottie-react";
import loadingAnimation from "assets/animations/add.json";
import { Box, Center } from "@chakra-ui/react";
import {
  collection,
  getDoc,
  setDoc,
  doc,
  serverTimestamp,
  query,
  where,
  getDocs,
  writeBatch,
  onSnapshot,
} from "firebase/firestore";
import { db } from "lib/firebase";

function App() {
  const { loading, auth, data, documents } = useStore(userProfile);
  const { uid, email: userEmail } = data || {};

  const authState = getAuth(app);

  useEffect(() => {
    const authListener = onAuthStateChanged(authState, (user) => {
      if (user) {
        setUserData(user);
        setIsAuthenticated(true);
      } else {
        setUserData({});
        setIsAuthenticated(false);
      }
    });

    return () => {
      authListener();
    };
  }, []);

  const fetchSignatureData = useCallback(async (requestId) => {
    try {
      const signatureDoc = await getDoc(doc(collection(db, "signatures"), requestId));
      return signatureDoc.exists() ? signatureDoc.data() || {} : {};
    } catch (error) {
      console.error("Error fetching signature data:", error);
      return {};
    }
  }, []);

  const findSignerByEmail = useCallback(
    (signers, email) =>
      Object.values(signers).find((signer) => signer.signerEmailAddress === email) || null,
    [],
    []
  );

  const countSignedSigners = useCallback(
    (signers) => Object.values(signers).filter((signer) => signer.statusCode === "success").length,
    [],
    []
  );

  const getOverallStatus = (signerStatuses) => {
    if (signerStatuses.includes("rejected")) return "rejected";
    if (signerStatuses.includes("awaiting_signature")) return "awaiting_signature";
    return "success";
  };

  const processSignatureData = async (
    requestId,
    data,
    fetchSignatureData,
    findSignerByEmail,
    countSignedSigners
  ) => {
    const signatureData = await fetchSignatureData(requestId);

    const signer = findSignerByEmail(signatureData.signers, data?.email);
    const signerData = signer && {
      documentName: signatureData?.documentName,
      createdTime: new Date(signatureData?.createdAt?.seconds * 1000).toDateString(),
      statusCode: signer.statusCode,
      signatureId: signer.signatureId,
      signedAt: signer.signedAt,
      pendingReview: signer.pendingReview,
      signatureRequestId: requestId,
      totalSigners: Object.keys(signatureData.signers).length,
      signedCount: countSignedSigners(signatureData.signers),
    };

    const ownerData = signatureData &&
      signatureData.createdBy === data?.uid && {
        ownerDocumentName: signatureData?.documentName || "N/A",
        ownerCreatedTime: new Date(signatureData.createdAt?.seconds * 1000).toDateString(),
        ownerStatusCode: getOverallStatus(
          Object.values(signatureData.signers).map((signer) => signer.statusCode)
        ),
        ownerTotalSigners: Object.keys(signatureData.signers).length,
        ownerSignedCount: countSignedSigners(signatureData.signers),
        ownerSignatureRequestId: requestId,
      };

    return { signerData, ownerData };
  };

  const processSnapshotForCounts = async (documentIds) => {
    let pendingSignatureCount = 0;
    let pendingYourSignatureCount = 0;
    let signedCardCount = 0;
    let pendingInsightsCount = 0;

    const fetchPromises = documentIds.map(async (document) => {
      const signatureRequest = await getDoc(doc(db, "signatures", document));
      const signers = signatureRequest?.data()?.signers || {};

      Object.values(signers).forEach((signer) => {
        if (signer.signerEmailAddress === userEmail && !signer.pendingReview) {
          pendingInsightsCount++;
        }
      });

      const pendingSignatures = Object.values(signers).filter(
        (signer) =>
          signer.signerEmailAddress === userEmail && signer.statusCode === "awaiting_signature"
      );

      pendingSignatureCount += pendingSignatures.length;

      const signedSignatures = Object.values(signers).filter(
        (signer) => signer.signerEmailAddress === userEmail && signer.statusCode === "success"
      );

      signedCardCount += signedSignatures.length;

      if (
        signatureRequest.data().createdBy === uid &&
        Object.values(signers).some((signer) => signer.statusCode === "awaiting_signature")
      ) {
        pendingYourSignatureCount++;
      }
    });

    await Promise.all(fetchPromises);

    return {
      pendingSignatureCount,
      pendingYourSignatureCount,
      signedCardCount,
      pendingInsightsCount,
    };
  };

  useEffect(() => {
    let unsubscribeUserSignatures;
    let unsubscribeSignatureRequests;

    const fetchData = async () => {
      if (auth && data) {
        const userSignatureRequestsRef = collection(db, "users", uid, "signatureRequests");
        const globalSignatureRequestsRef = collection(db, "signatures");

        const initialSnapshot = await getDocs(userSignatureRequestsRef);
        await processData(initialSnapshot.docs.map((docSnapshot) => docSnapshot.id));

        unsubscribeUserSignatures = onSnapshot(userSignatureRequestsRef, async (querySnapshot) => {
          await processData(querySnapshot.docs.map((docSnapshot) => docSnapshot.id));
        });

        unsubscribeSignatureRequests = onSnapshot(
          globalSignatureRequestsRef,
          async (querySnapshot) => {
            const userDocs = await getDocs(userSignatureRequestsRef);
            const documentIds = userDocs.docs.map((docSnapshot) => docSnapshot.id);
            await processData(documentIds);
          }
        );

        return () => {
          unsubscribeUserSignatures();
          unsubscribeSignatureRequests();
        };
      }
    };

    const processData = async (signatureRequestIds) => {
      const allData = await Promise.all(
        signatureRequestIds.map((requestId) =>
          processSignatureData(
            requestId,
            data,
            fetchSignatureData,
            findSignerByEmail,
            countSignedSigners
          )
        )
      );

      const newSignersData = allData.map((item) => item.signerData).filter(Boolean);
      const newOwnerData = allData.map((item) => item.ownerData).filter(Boolean);

      const {
        pendingSignatureCount,
        pendingYourSignatureCount,
        signedCardCount,
        pendingInsightsCount,
      } = await processSnapshotForCounts(signatureRequestIds);

      const finalDocument = {
        signersData: newSignersData,
        ownerData: newOwnerData,
        counts: {
          pendingSignature: pendingSignatureCount,
          pendingYourSignature: pendingYourSignatureCount,
          signedCard: signedCardCount,
          pendingInsights: pendingInsightsCount,
        },
      };

      setDocuments(finalDocument);
    };

    fetchData();
  }, [auth]);

  if (loading)
    return (
      <Center h="100vh" w="100vw">
        <Box w="400px" h="400px">
          <Lottie animationData={loadingAnimation} />
        </Box>
      </Center>
    );

  return (
    <BrowserRouter>
      <Route exact path={`/`} component={LandingLayout} />
      {auth ? (
        <Switch>
          <Route path={`/admin`} component={AdminLayout} />
          <Redirect from="/auth" to="/admin/dashboard" />
        </Switch>
      ) : (
        <Switch>
          <Route path={`/auth`} component={AuthLayout} />
          <Redirect from="/admin" to="/auth/sign-in" />
        </Switch>
      )}
    </BrowserRouter>
  );
}

export default App;
